/**
 * Usage analytics (PostHog EU cloud): the switch and its settings.
 *
 * OFF unless the build sets all of:
 * - `VITE_USAGE_ANALYTICS=on`  (anything else, or unset = off);
 * - `VITE_POSTHOG_KEY`         the project key. ONE PostHog project for all
 *                              environments (owner 2026-10-15, free plan);
 *                              which builds switch it on is decided per
 *                              environment (prod: real usage; dev: only while
 *                              the setup is tested; demo: off);
 * - `VITE_POSTHOG_HOST`        where events are sent: a neutral path on OUR own
 *                              domain (e.g. `/ingest`) that the web server forwards
 *                              to eu.i.posthog.com. Same origin, so the
 *                              Content-Security-Policy needs no new source.
 * `VITE_POSTHOG_UI_HOST` is the PostHog app itself (default https://eu.posthog.com).
 *
 * `VITE_POSTHOG_REPLAY=on` (anything else, or unset = off) adds session
 * replay, masked (usage/replay.ts). It has no effect while usage analytics
 * itself is off.
 *
 * `VITE_APP_ENV` (`dev` | `demo` | `production`, set by the deploy workflows;
 * missing = `unknown`) names the build's environment. Because all environments
 * share the one project, every event carries it as the `environment` property,
 * and outside production the person and organisation ids are prefixed with it
 * (`dev:812`, organisation `dev:63`): the databases are separate, so the same
 * number is a different person or organisation there and must never merge with
 * a production one. Production keeps the plain ids.
 *
 * While it is off nothing of the SDK is loaded: `usage/client.ts` imports it
 * with a dynamic `import()` only after this module answered "on".
 */
export interface UsageEnv {
  VITE_APP_ENV?: string;
  VITE_USAGE_ANALYTICS?: string;
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
  VITE_POSTHOG_UI_HOST?: string;
  VITE_POSTHOG_REPLAY?: string;
}

export interface UsageConfig {
  key: string;
  host: string;
  uiHost: string;
  /** the build's environment (`dev`, `demo`, `production`, ...), `unknown` when not set */
  environment: string;
  /** session replay (masked), own switch; only ever true while usage analytics is on */
  replay: boolean;
}

export const PRODUCTION = 'production';
export const UNKNOWN_ENVIRONMENT = 'unknown';
/** An environment name: lower case, starts with a letter (it is also the id prefix). */
export const ENVIRONMENT_NAME = /^[a-z][a-z0-9_-]{0,31}$/;

export const DEFAULT_POSTHOG_UI_HOST = 'https://eu.posthog.com';

const text = (value: unknown): string => String(value ?? '').trim();

/** The environment of the build as sent with every event; anything that is not a plain name is `unknown`. */
export const resolveEnvironment = (value: unknown): string => {
  const name = text(value).toLowerCase();
  return ENVIRONMENT_NAME.test(name) ? name : UNKNOWN_ENVIRONMENT;
};

/**
 * The id sent for a person or an organisation: the plain id in production,
 * `<environment>:<id>` everywhere else, so ids of different databases never merge.
 */
export const usageId = (environment: string, id: number | string): string =>
  environment === PRODUCTION ? String(id) : `${environment}:${id}`;

/** The settings when usage analytics is switched on and complete, else null (off). */
export const resolveUsageConfig = (env: UsageEnv): UsageConfig | null => {
  if (text(env.VITE_USAGE_ANALYTICS).toLowerCase() !== 'on') return null;
  const key = text(env.VITE_POSTHOG_KEY);
  const host = text(env.VITE_POSTHOG_HOST).replace(/\/+$/, '');
  if (!key || !host) return null;
  return {
    key,
    host,
    uiHost: text(env.VITE_POSTHOG_UI_HOST) || DEFAULT_POSTHOG_UI_HOST,
    environment: resolveEnvironment(env.VITE_APP_ENV),
    replay: text(env.VITE_POSTHOG_REPLAY).toLowerCase() === 'on',
  };
};

/** Read on every call (cheap), so a test can switch it with `vi.stubEnv`. */
export const getUsageConfig = (): UsageConfig | null =>
  resolveUsageConfig({
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_USAGE_ANALYTICS: import.meta.env.VITE_USAGE_ANALYTICS,
    VITE_POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
    VITE_POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST,
    VITE_POSTHOG_UI_HOST: import.meta.env.VITE_POSTHOG_UI_HOST,
    VITE_POSTHOG_REPLAY: import.meta.env.VITE_POSTHOG_REPLAY,
  });

export const usageEnabled = (): boolean => getUsageConfig() !== null;
